import React, { useEffect, useState } from "react";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import { taxonomyList } from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import Textarea from "../../../form/Textarea";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectSingleImage from "../../../form/SelectSingleImage";
import SelectImageGallery from "../../../form/SelectImageGallery";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import DatePicker from "../../../form/DatePicker";

const validation = {
  title: { required: true, message: "title is required" },
  content: { required: true, message: "content is required" },
};

const ManageJob = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [coverMedia, setCoverMedia] = useState("");
  const [categories, setCategories] = useState([]);
  const [salaryRange, setSalaryRange] = useState([]);
  const [skills, setSkills] = useState([]);
  const [locationType, setLocationType] = useState("onsite");
  const [locations, setLocations] = useState([]);

  const { values, handleChange, handleSubmit, errors, setFieldsValue, formValidate } = useForm({ validationRules: validation });

  const { data: details } = useQuery({
    queryKey: ["post-details", id],
    queryFn: async () => {
      const res = await postDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        department: details?.job?.department?._id || "",
        jobType: details?.job?.jobType || "",
        jobExperienceLevel: details?.job?.experience?._id || "",
        jobSkills: details?.job?.skills?.map((item) => item._id) || [],
        jobOpening: details?.job?.openings || 1,
      });
      setLocations(details?.location);
      setLocationType(details?.locationType);
      setCategories(details?.categories);
      setSalaryRange(details?.job?.budget?._id);
      setSkills(details?.job?.skills);
      setCoverMedia(details?.coverMedia);
      setSelectedMedia(details?.mediaGallery);
      setTags(
        details.tagDetails.map((item) => ({
          label: item.title,
          _id: item._id,
        }))
      );
    }
  }, [details]);
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const media = selectedMedia?.reduce((acc, item, index) => {
        acc[`mediaGallery[${index}]`] = item?._id;
        return acc;
      }, {});

      const tag = tags?.reduce((acc, tag, index) => {
        acc[`tags[${index}]`] = tag;
        return acc;
      }, {});

      // const departments = val?.departments?.reduce((acc, category, index) => {
      //   acc[`departments[${index}]`] = category;
      //   return acc;
      // }, {});

      return await postAddEdit({
        title: val?.title,
        type: "standard-job",
        content: val?.content,
        status: val?.status,
        excerpt: val?.excerpt,
        jobDepartment: val?.department,
        jobExperienceLevel: val?.jobExperienceLevel,
        jobType: val?.jobType,
        jobOpening: val?.jobOpening,
        jobSkills: skills,
        jobMode: "standard",
        jobBudget: salaryRange,
        coverMedia: coverMedia?._id,
        categories: categories,
        locationType:locationType,
        location:locations,
        ...(details?._id && { id: details._id }),
        ...media,
        ...tag,
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/jobs`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const { data: jobDepartmentData } = useQuery({
    queryKey: ["department"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "department",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });
  const { data: jobExperienceData } = useQuery({
    queryKey: ["experience"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "experience",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });
  const { data: jobSkillData } = useQuery({
    queryKey: ["skill"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "skill",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: salaryData } = useQuery({
    queryKey: ["salaray"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "salary-range",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "standard-job-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "standard-job-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });
  const handleLocationChange = (e, index) => {
    const { name, value } = e.target;
    const field = name.split("-")[0]; // Extract field name (e.g., "city")
    setLocations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCoordinatesChange = (e, index) => {
    const value = e.target.value;
    const [lat, lng] = value.split(",").map(Number);
    setLocations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], coordinates: [lat, lng] };
      return updated;
    });
  };

  const handleLatLngChange = (e, index) => {
    const { name, value } = e.target;
    setLocations((prev) => {
      const updated = [...prev];
      const coords = updated[index].coordinates || [null, null];
      if (name === "latitude") coords[0] = parseFloat(value);
      if (name === "longitude") coords[1] = parseFloat(value);
      updated[index] = { ...updated[index], coordinates: coords };
      return updated;
    });
  };

  const handleSubmitWrapper = (event) => {
    handleSubmit(event, (values) => {
      console.log("Submitting values:", values);
      mutate(values);
    });
  };

  return (
    <>
      <form onSubmit={(event) => handleSubmitWrapper(event, mutate)}>
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">{details?._id ? "Update Jobs" : "Create Jobs"}</div>

          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel={details?._id ? "Update Job" : "Save Job"}
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonFunction={() => navigate("/admin/jobs")}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              onChange={handleChange}
              inputName={"title"}
              required={false}
              value={values?.title}
              {...formValidate(errors, "title")}
            />
            <Editor
              label={"Content"}
              height={200}
              value={values?.content}
              placeholder={"Write Something"}
              onChange={(val) =>
                handleChange({
                  target: { name: "content", value: val },
                })
              }
              {...formValidate(errors, "content")}
            />
            <Textarea
              label="Excerpt"
              inputPlaceholder="Write excerpt"
              inputClasses="!h-28"
              inputName="excerpt"
              inputValue={values?.excerpt || ""}
              onChange={handleChange}
            />
            <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery label="Gallery  " values={selectedMedia} onChange={(newMedia) => setSelectedMedia(newMedia)} isSingle={false} />
            </div>
            <FlexibleSelect
              label="Location Type"
              placeholder="Choose Location Type"
              onChange={(e) => {
                setLocationType(e);
                if (e === "reomte") setLocations([]); // Reset if onsite
              }}
              selectedValue={locationType}
              dropdownData={[
                { name: "Onsite", value: "onsite" },
                { name: "Remote", value: "remote" },
                { name: "Hybrid", value: "hybrid" },
              ]}
            />

            {locationType !== "remote" && (
              <div className="space-y-4 mt-4">
                {locations.map((loc, index) => (
                  <div key={index} className="p-4 border border-gray-300 rounded-md space-y-2 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold">Address {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => setLocations((prev) => prev.filter((_, i) => i !== index))}
                        className="text-red-500 text-xs"
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>

                    <Input label="Name" inputName="name" value={loc.name || ""} onChange={(e) => handleLocationChange(e, index)} />
                    <Input label="Address 1" inputName="address1" value={loc.address1 || ""} onChange={(e) => handleLocationChange(e, index)} />
                    <Input label="Address 2" inputName="address2" value={loc.address2 || ""} onChange={(e) => handleLocationChange(e, index)} />
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="City" inputName="city" value={loc.city || ""} onChange={(e) => handleLocationChange(e, index)} />
                      <Input label="State" inputName="state" value={loc.state || ""} onChange={(e) => handleLocationChange(e, index)} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <Input label="Country" inputName="country" value={loc.country || ""} onChange={(e) => handleLocationChange(e, index)} />
                      <Input label="Zipcode" inputName="zipcode" value={loc.zipcode || ""} onChange={(e) => handleLocationChange(e, index)} />
                    </div>

                    <Input label="Phone" inputName="contactPhone" value={loc.contactPhone || ""} onChange={(e) => handleLocationChange(e, index)} />
                    <Input label="Landmark" inputName="landmark" value={loc.landmark || ""} onChange={(e) => handleLocationChange(e, index)} />

                    {/* Coordinates and defaultAddress */}
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Coordinates(Latitude)"
                        inputName="latitude"
                        value={loc.coordinates?.[0] || ""}
                        onChange={(e) => handleLatLngChange(e, index)}
                      />
                      <Input
                        label="Coordinates(Longitude)"
                        inputName="longitude"
                        value={loc.coordinates?.[1] || ""}
                        onChange={(e) => handleLatLngChange(e, index)}
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`defaultAddress-${index}`}
                        checked={loc.defaultAddress || false}
                        onChange={() =>
                          setLocations((prev) => {
                            const updated = [...prev];
                            updated[index].defaultAddress = !updated[index].defaultAddress;
                            return updated;
                          })
                        }
                      />
                      {
                        loc.defaultAddress?<label htmlFor={`defaultAddress-${index}`} className="text-sm">
                        Default Address
                      </label> : <label htmlFor={`defaultAddress-${index}`} className="text-sm">
                        Set as Default Address
                      </label>
                      }
                    </div>
                  </div>
                ))}

                <Button buttonLabel="+ Add Location" buttonFunction={() => setLocations([...locations, {}])} buttonClasses="!bg-secondary !text-white" />
              </div>
            )}
            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4 mt-10">
                <Button
                  buttonType="submit"
                  buttonLabel={details?._id ? "Update Job" : "Save Job"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonFunction={() => navigate("/admin/jobs")}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Featured Image"
                size="lg"
                value={coverMedia}
                onChange={(img) => setCoverMedia(img)}
                onRemove={() => setCoverMedia(null)}
              />
            </div>
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Date"}
                placeholder={"Choose publish date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "date",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.createdAt,
                  endDate: values?.createdAt,
                }}
                {...formValidate(errors, "createdAt")}
              />

              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                  { name: "Draft", value: "draft" },
                  { name: "Private", value: "private" },
                ]}
                {...formValidate(errors, "status")}
              />

              <FlexibleSelect
                label={"Job Categories"}
                isSearch={true}
                dropdownData={categoryData?.data?.map((item) => {
                  return {
                    name: item?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={values?.categories}
                onChange={setCategories}
                type="multiple"
                placeholder="Choose category"
              />

              <FlexibleSelect
                label={"Job Type"}
                placeholder={"Choose job type"}
                onChange={(e) => handleChange({ target: { name: "jobType", value: e } })}
                selectedValue={values?.job?.jobType}
                dropdownData={[
                  { name: "Full Time", value: "full-time" },
                  { name: "Part Time", value: "part-time" },
                  { name: "Contract", value: "contract" },
                  { name: "Internship", value: "internship" },
                  { name: "Remote", value: "remote" },
                ]}
                {...formValidate(errors, "status")}
              />

              <FlexibleSelect
                label={"Job Departments"}
                placeholder={"Choose departments"}
                onChange={(e) => handleChange({ target: { name: "department", value: e } })}
                selectedValue={values?.department}
                dropdownData={jobDepartmentData?.data?.map((item) => {
                  return {
                    name: item?.title,
                    value: item?._id,
                  };
                })}
                {...formValidate(errors, "department")}
              />

              <FlexibleSelect
                label={"Salary Range"}
                dropdownData={salaryData?.data?.map((item) => {
                  return {
                    name: `${item?.range?.unit === "usd" ? "$" : "₹"}${
                      item?.range?.min >= 1000 ? item?.range?.min / 1000 + "k" : item?.range?.min
                    }  -   ${item?.range?.unit === "usd" ? "$" : "₹"}${item?.range?.max >= 1000 ? item?.range?.max / 1000 + "k" : item?.range?.max}`,
                    value: item?._id,
                  };
                })}
                selectedValue={salaryRange}
                onChange={setSalaryRange}
                placeholder="Choose Salary Range"
              />

              <FlexibleSelect
                label={"Experience Level"}
                placeholder={"Choose experience level"}
                onChange={(e) => handleChange({ target: { name: "jobExperienceLevel", value: e } })}
                selectedValue={values?.jobExperienceLevel}
                dropdownData={jobExperienceData?.data?.map((item) => {
                  return {
                    name: item?.title,
                    value: item?._id,
                  };
                })}
                {...formValidate(errors, "jobExperienceLevel")}
              />

              <FlexibleSelect
                label={"Skills"}
                isSearch={true}
                dropdownData={jobSkillData?.data?.map((item) => {
                  return {
                    name: item?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={values?.jobSkills}
                onChange={setSkills}
                type="multiple"
                placeholder="Choose skills"
              />

              <Input
                label="Number of Openings"
                inputPlaceholder="Enter position opened"
                inputType="number"
                onChange={handleChange}
                inputName={"jobOpening"}
                required={false}
                value={values?.jobOpening}
              />

              <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label: item.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageJob;
